/**
 * Boli Live - Quantity Selector Component
 *
 * Reusable +/- quantity input for product pages.
 */

import { Minus, Plus } from 'lucide-react';
import { cn } from '../lib/utils';

interface QuantitySelectorProps {
  value: number;
  min?: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  className?: string;
}

export function QuantitySelector({
  value,
  min = 1,
  max,
  onChange,
  disabled = false,
  className,
}: QuantitySelectorProps) {
  const canDecrement = value > min && !disabled;
  const canIncrement = value < max && !disabled;

  const handleDecrement = () => {
    if (canDecrement) {
      onChange(value - 1);
    }
  };

  const handleIncrement = () => {
    if (canIncrement) {
      onChange(value + 1);
    }
  };

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <button
        type="button"
        onClick={handleDecrement}
        disabled={!canDecrement}
        className={cn(
          'w-9 h-9 flex items-center justify-center rounded-md border transition-colors',
          canDecrement
            ? 'border-border bg-secondary hover:bg-muted text-foreground'
            : 'border-border/50 bg-muted/50 text-muted-foreground cursor-not-allowed'
        )}
        aria-label="Decrease quantity"
      >
        <Minus className="w-4 h-4" />
      </button>

      <div className="w-12 h-9 flex items-center justify-center border border-border rounded-md bg-background font-medium text-foreground">
        {value}
      </div>

      <button
        type="button"
        onClick={handleIncrement}
        disabled={!canIncrement}
        className={cn(
          'w-9 h-9 flex items-center justify-center rounded-md border transition-colors',
          canIncrement
            ? 'border-border bg-secondary hover:bg-muted text-foreground'
            : 'border-border/50 bg-muted/50 text-muted-foreground cursor-not-allowed'
        )}
        aria-label="Increase quantity"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}
